import { useCallback, useMemo, useState } from "react";
import Button from "app/storybookComponents/Button";
import { useAppSelector } from "utils/redux/hooks";
import {
  selectConfigCatFlag,
  selectIsCurrentUserAdmin,
} from "app/containers/Global/slice";
import Loading from "app/storybookComponents/Loading";
import ActivityMonitorContainerEmptyContent from "app/components/ActivityMonitor/ActivityMonitorContainerEmptyContent";
import ActivityMonitorContainer from "app/components/ActivityMonitor/ActivityMonitorContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  CHECK_IN_TIME_RANGE_OPTIONS,
  WEEKLY_CHECK_IN_FEATURE_NAME,
} from "../constants";
import {
  getCoachBoWeeklyCheckInActivityReportToActivityMonitorBoxProps,
  getWeeklyCheckInActivityTimeIntervalKey,
  getWeeklyCheckinActivityLineChartData,
  getWeeklyCheckinActivityLineEmptyState,
} from "../helpers";
import { useNavigate } from "react-router-dom";
import SimpleLineChart from "app/storybookComponents/Charts/SimpleLineChart";
import ChartLegend from "app/storybookComponents/Charts/ChartLegend";
import { TooltipItem } from "chart.js";
import SimpleCollapsibleCard from "app/storybookComponents/CollapsibleCards/SimpleCollapsibleCard";
import { trackFeatureViewed } from "utils/trackingFunctions";
import CircularTabs from "app/storybookComponents/Tabs/CircularTabs";
import { CONFIG_CAT_FLAGS } from "app/components/GlobalNavbar/constants";
import { responseStatus } from "utils/types";
import {
  CoachBoWeeklyCheckInActivityReports,
  WeeklyCheckInLineChartState,
} from "../types";
import WeeklyCheckInWelcomeModal from "app/components/WeeklyCheckInComponents/WeeklyCheckInWelcomeModal";
import { selectPeopleWithDeliverabilityIssues } from "app/containers/Dashboard/slice";
import { WeeklyCheckInMerchandisingType } from "app/components/WeeklyCheckInComponents/types";
import { selectWeeklyCheckInCompanySettings } from "../slice";

interface Props {
  coachBoWeeklyCheckInActivityReports: CoachBoWeeklyCheckInActivityReports;
  getCoachBoWeeklyCheckInActivityReportStatus: responseStatus;
  showWeeklyCheckInActivityEmptyState: boolean;
  weeklyCheckInLineChartData: WeeklyCheckInLineChartState;
  fetchingWeeklyCheckInLineChartDataStatus: responseStatus;
  getShowWeeklyCheckInActivityEmptyStateStatus: responseStatus;
  viewActivityPath: string;
  weeklyCheckInActivityTimeInterval?: string;
  setWeeklyCheckInActivityTimeInterval?: (timeInterval: string) => void;
  lineChartTimeInterval?: string;
  setLineChartTimeInterval?: (timeInterval: string) => void;
  lineChartNavigationIndex?: number;
  setLineChartNavigationIndex?: (index: number) => void;
  onGetWeeklyCheckInLineChartData: ({
    timeInterval,
    index,
  }: {
    timeInterval: string;
    index: number;
  }) => void;
  headerCornerContent?: React.ReactNode;
  showingOnlySubordinates?: boolean;
  userHasNoSubordinates?: boolean;
  additionalFooterContent?: React.ReactNode;
  actionButtonEmptyContent?: React.ReactNode | null;
  merchandisingType: WeeklyCheckInMerchandisingType;
  additionalBanners?: React.ReactNode;
}

const WeeklyCheckInActivitySnapshot = ({
  coachBoWeeklyCheckInActivityReports,
  getCoachBoWeeklyCheckInActivityReportStatus,
  showWeeklyCheckInActivityEmptyState,
  weeklyCheckInLineChartData,
  fetchingWeeklyCheckInLineChartDataStatus,
  getShowWeeklyCheckInActivityEmptyStateStatus,
  viewActivityPath,
  weeklyCheckInActivityTimeInterval = "Past 7 days",
  setWeeklyCheckInActivityTimeInterval = () => {},
  lineChartTimeInterval = "Past 30 days",
  setLineChartTimeInterval,
  lineChartNavigationIndex = 1,
  setLineChartNavigationIndex,
  onGetWeeklyCheckInLineChartData,
  headerCornerContent = null,
  showingOnlySubordinates,
  userHasNoSubordinates,
  additionalFooterContent = null,
  actionButtonEmptyContent = null,
  merchandisingType,
  additionalBanners,
}: Readonly<Props>) => {
  const navigate = useNavigate();
  // ------------------------------- Selectors --------------------------------------------- //

  const weeklyCheckInConfigCatFlag = useAppSelector(
    selectConfigCatFlag(CONFIG_CAT_FLAGS.weeklyCheckIn.savedFlagName)
  );
  const peopleWithDeliverabilityIssues = useAppSelector(
    selectPeopleWithDeliverabilityIssues
  );
  const isAdmin = useAppSelector(selectIsCurrentUserAdmin);
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );

  // ------------------------------------- Component States ------------------------------------- //
  const [isLineChartCollapsed, setIsLineChartCollapsed] = useState(true);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // ------------------------------------- Memo States ------------------------------------- //
  const activeCoachBoWeeklyCheckInActivityReport = useMemo(() => {
    const activeKey =
      CHECK_IN_TIME_RANGE_OPTIONS[weeklyCheckInActivityTimeInterval];

    return coachBoWeeklyCheckInActivityReports[activeKey];
  }, [weeklyCheckInActivityTimeInterval, coachBoWeeklyCheckInActivityReports]);

  const activeCoachBoCheckInLineChartData = useMemo(() => {
    const dataKey = getWeeklyCheckInActivityTimeIntervalKey(
      lineChartTimeInterval
    );
    return (
      dataKey && weeklyCheckInLineChartData[dataKey]?.[lineChartNavigationIndex]
    );
  }, [
    weeklyCheckInLineChartData,
    lineChartTimeInterval,
    lineChartNavigationIndex,
  ]);

  const showEmailDeliverabilityBanner = useMemo(() => {
    if (showingOnlySubordinates) {
      return false;
    }

    const isThereAnyPeopleWithIssuesWithInLast30Days =
      peopleWithDeliverabilityIssues.some((peopleWithDeliverabilityIssue) => {
        const { mostRecentBounceDate } = peopleWithDeliverabilityIssue;
        if (!mostRecentBounceDate) {
          return false;
        }
        const date = new Date(mostRecentBounceDate);
        const today = new Date();
        const diffTime = Math.abs(today.getTime() - date.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays <= 30;
      });
    return isThereAnyPeopleWithIssuesWithInLast30Days;
  }, [showingOnlySubordinates, peopleWithDeliverabilityIssues]);

  // ------------------------------------- Render ------------------------------------- //

  const getActionButton = () => {
    const { completionInfo } = activeCoachBoWeeklyCheckInActivityReport ?? {};
    if (!completionInfo?.completedCount && !completionInfo?.totalCount) {
      return (
        <div className="warning-banner light-blue border-0">
          {actionButtonEmptyContent ?? (
            <p>
              No Check-Ins received yet. Please check back soon.{" "}
              Once responses are received, score averages will be available
              here.
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="row-gap-8px">
        <Button
          variant="tertiary-blue"
          onClick={() => {
            navigate(viewActivityPath);
          }}
          className="activity-monitor-container__footer-button"
        >
          View activity{" "}
          <FontAwesomeIcon icon={["fas", "arrow-right"]} className="ms-2" />
        </Button>
        {!!isAdmin && (
          <Button
            variant="tertiary-blue"
            onClick={() => {
              navigate(
                "/AdminConsole/Dashboard?dashboardSubpage=EmailDeliverabilityIssues"
              );
            }}
            className="activity-monitor-container__footer-button"
          >
            View email deliverability activity{" "}
            <FontAwesomeIcon icon={["fas", "arrow-right"]} className="ms-2" />
          </Button>
        )}
      </div>
    );
  };

  const getToolTipBody = useCallback(
    (tooltipItems: TooltipItem<"line">[]) => {
      const index = tooltipItems[0].dataIndex;
      const countArray = activeCoachBoCheckInLineChartData?.counts ?? [];
      const count = countArray[index] ?? 0;
      return `Number of responses: ${count}\nScore: ${tooltipItems[0].formattedValue}`;
    },
    [activeCoachBoCheckInLineChartData]
  );

  const onLineChartNavigate = useCallback(
    (newIndex: number) => {
      const index = Math.max(1, newIndex);
      setLineChartNavigationIndex?.(index);
      const dataKey = getWeeklyCheckInActivityTimeIntervalKey(
        lineChartTimeInterval
      );

      if (dataKey) {
        onGetWeeklyCheckInLineChartData({
          timeInterval: dataKey,
          index,
        });
      }
    },
    [
      lineChartTimeInterval,
      onGetWeeklyCheckInLineChartData,
      setLineChartNavigationIndex,
    ]
  );

  const getLineChartNavigation = useCallback(() => {
    // "Past 30 days" is at most one 28-day monthly cycle, which can never
    // clear the trend chart's 2-data-point minimum — don't offer an option
    // that's guaranteed to land on the empty state.
    const labelTags =
      weeklyCheckInCompanySettings?.checkInFrequency === "monthly"
        ? ["Past 365 days"]
        : ["Past 30 days", "Past 365 days"];
    const activeTag = lineChartTimeInterval;
    return (
      <div style={{ marginTop: "-20px" }}>
        <CircularTabs
          tabs={labelTags.map((tag) => ({ label: tag }))}
          selectedTab={activeTag ?? undefined}
          onTabSelected={(tag) => {
            setLineChartTimeInterval?.(tag);
            setLineChartNavigationIndex?.(1);
            const dataKey = getWeeklyCheckInActivityTimeIntervalKey(tag);
            if (dataKey) {
              onGetWeeklyCheckInLineChartData({
                timeInterval: dataKey,
                index: 1,
              });
            }
          }}
        />
      </div>
    );
  }, [
    lineChartTimeInterval,
    setLineChartNavigationIndex,
    setLineChartTimeInterval,
    onGetWeeklyCheckInLineChartData,
    weeklyCheckInCompanySettings?.checkInFrequency,
  ]);

  const lineChart = useMemo(() => {
    if (fetchingWeeklyCheckInLineChartDataStatus === "loading") {
      return <Loading />;
    }
    if (weeklyCheckInCompanySettings?.visibilityMode !== "open") {
      return null;
    }
    const lineChartData = getWeeklyCheckinActivityLineChartData(
      activeCoachBoCheckInLineChartData
    );

    if (!lineChartData || lineChartData.hasAtLeastTwoDataPoints === false) {
      return (
        <>
          {getLineChartNavigation()}
          {getWeeklyCheckinActivityLineEmptyState(
            lineChartTimeInterval,
            lineChartData?.hasAtLeastTwoDataPoints === false
          )}
        </>
      );
    }
    const { dataValues, labels } = lineChartData;
    const { hasNextTimePeriodData, hasPreviousTimePeriodData } =
      activeCoachBoCheckInLineChartData ?? {};

    return (
      <>
        {getLineChartNavigation()}
        <SimpleLineChart
          dataValues={dataValues}
          labels={labels}
          canvasId="weekly-check-in-activity-line-chart"
          maxYScale={5.1}
          stepSize={1}
          tooltipCallbacks={{
            title: () => "",
            label: () => "",
            afterBody: getToolTipBody,
          }}
        />
        <div className="d-flex justify-content-between">
          <Button
            variant="secondary-blue"
            disabled={!hasPreviousTimePeriodData}
            onClick={() => {
              onLineChartNavigate(lineChartNavigationIndex + 1);
            }}
          >
            <FontAwesomeIcon icon="arrow-left" className="me-2" />
            Previous
          </Button>
          <ChartLegend
            labels={["Average Mood", "Average Progress", "Average Readiness"]}
          />
          <Button
            variant="secondary-blue"
            disabled={!hasNextTimePeriodData}
            onClick={() => {
              onLineChartNavigate(lineChartNavigationIndex - 1);
            }}
          >
            Next
            <FontAwesomeIcon icon="arrow-right" className="ms-2" />
          </Button>
        </div>
      </>
    );
  }, [
    lineChartNavigationIndex,
    lineChartTimeInterval,
    activeCoachBoCheckInLineChartData,
    fetchingWeeklyCheckInLineChartDataStatus,
    getLineChartNavigation,
    getToolTipBody,
    onLineChartNavigate,
    weeklyCheckInCompanySettings,
  ]);

  const getEmptyState = (): {
    isEmpty: boolean;
    emptyStateContent: React.ReactElement;
  } => {
    return {
      isEmpty:
        showWeeklyCheckInActivityEmptyState &&
        getCoachBoWeeklyCheckInActivityReportStatus !== "loading" &&
        getShowWeeklyCheckInActivityEmptyStateStatus !== "loading",
      emptyStateContent: (
        <ActivityMonitorContainerEmptyContent
          title={`Start ${WEEKLY_CHECK_IN_FEATURE_NAME}s`}
          button={
            <Button
              variant="secondary-blue"
              className="white-button"
              onClick={() => setIsPreviewModalOpen(true)}
            >
              See how it works
            </Button>
          }
          description={`AI-powered ${WEEKLY_CHECK_IN_FEATURE_NAME.toLowerCase()}s enable managers to get summarized reports on team mood, status, and blockers while tracking trends over time and highlighting key conversations to have.`}
        />
      ),
    };
  };

  const getLineChartSection = () => {
    if (weeklyCheckInCompanySettings?.visibilityMode !== "open") {
      return null;
    }
    return (
      <SimpleCollapsibleCard
        className="column-gap-20px"
        title={
          <p className="grey-text fw-bold">
            Mood, Progress, and Readiness Over Time
          </p>
        }
        isOpen={!isLineChartCollapsed}
        setIsOpen={() => {
          setIsLineChartCollapsed(!isLineChartCollapsed);
          trackFeatureViewed("Weekly Check-In Activity Line Chart", {
            timeInterval: weeklyCheckInActivityTimeInterval,
            location: "dashboard",
          });
        }}
        collapseText="Hide chart"
        expandText="Show chart"
      >
        <div className="column-gap-20px">{lineChart}</div>
      </SimpleCollapsibleCard>
    );
  };

  const getFooter = () => (
    <>
      {getLineChartSection()}
      {additionalFooterContent}
      {getActionButton()}
    </>
  );

  const onTagClick = (tag: string) => {
    setWeeklyCheckInActivityTimeInterval(tag);
    setLineChartNavigationIndex?.(1);
    const dataKey = getWeeklyCheckInActivityTimeIntervalKey(tag);
    if (dataKey) {
      onGetWeeklyCheckInLineChartData({ timeInterval: dataKey, index: 1 });
    }
  };

  const weeklyCheckInTimeInterval = weeklyCheckInActivityTimeInterval.includes(
    "Past"
  )
    ? weeklyCheckInActivityTimeInterval.split("Past")[1].trim().toLowerCase()
    : weeklyCheckInActivityTimeInterval.toLowerCase();

  const { isEmpty, emptyStateContent } = getEmptyState();

  const getTitle = () => {
    if (userHasNoSubordinates) {
      return `Your ${WEEKLY_CHECK_IN_FEATURE_NAME}s`;
    }
    return `${WEEKLY_CHECK_IN_FEATURE_NAME}s Activity`;
  };

  const getBanners = () => {
    const banners: React.ReactNode[] = [];
    if (additionalBanners) {
      banners.push(additionalBanners);
    }
    if (showEmailDeliverabilityBanner) {
      banners.push(
        <div className="warning-banner justify-content-between d-flex">
          <div className="row-gap-10px">
            <FontAwesomeIcon icon="exclamation-triangle" />
            <div>
              <p>
                <b>Email delivery issues</b>
              </p>
              <p>This may be affecting check-in completion.</p>
            </div>
          </div>
          <div className="row-gap-10px">
            <Button
              onClick={() => {
                navigate(
                  "/AdminConsole/Dashboard?dashboardSubpage=EmailDeliverabilityIssues"
                );
              }}
            >
              View Details
            </Button>
          </div>
        </div>
      );
    }
    return banners;
  };

  if (!weeklyCheckInConfigCatFlag) {
    return null;
  }

  return (
    <>
      <WeeklyCheckInWelcomeModal
        show={isPreviewModalOpen}
        onHide={() => setIsPreviewModalOpen(false)}
        hidePilotButton
        type={merchandisingType}
      />
      <ActivityMonitorContainer
        title={getTitle()}
        labelTags={Object.keys(CHECK_IN_TIME_RANGE_OPTIONS)}
        activeTag={weeklyCheckInActivityTimeInterval}
        onTagClick={onTagClick}
        boxPropsOrOverrides={getCoachBoWeeklyCheckInActivityReportToActivityMonitorBoxProps(
          activeCoachBoWeeklyCheckInActivityReport,
          weeklyCheckInTimeInterval,
          showingOnlySubordinates,
          userHasNoSubordinates,
          weeklyCheckInCompanySettings?.visibilityMode !== "open"
        )}
        footer={getFooter()}
        timeInterval={weeklyCheckInTimeInterval}
        titleTag={
          <div>
            <FontAwesomeIcon icon={["far", "sparkles"]} />
            <span className="label-tag info-sapphire">Powered by AI</span>
          </div>
        }
        isEmpty={isEmpty}
        emptyStateContent={emptyStateContent}
        headerCornerContent={headerCornerContent}
        banners={getBanners()}
        isLoading={
          getCoachBoWeeklyCheckInActivityReportStatus === "loading" ||
          getShowWeeklyCheckInActivityEmptyStateStatus === "loading"
        }
        loadingSkeletonCount={3}
      />
    </>
  );
};

export default WeeklyCheckInActivitySnapshot;
